import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { MainLayout } from "@/components/layout/MainLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { uiText } from "@/lib/i18n/uiText";

export const Route = createFileRoute("/unsubscribe")({
  validateSearch: (s: Record<string, unknown>) => ({ t: typeof s["t"] === "string" ? s["t"] : "" }),
  head: () => ({
    meta: [
      { title: "Unsubscribe from Statlign emails" },
      { name: "description", content: "Stop Statlign recap and alert emails with one click." },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Unsubscribe from Statlign emails" },
      { property: "og:description", content: "Stop Statlign recap and alert emails with one click." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: () => (
    <MainLayout>
      <UnsubscribePage />
    </MainLayout>
  ),
});

function UnsubscribePage() {
  const { t } = Route.useSearch();
  const [state, setState] = useState<"idle" | "busy" | "done" | "error">("idle");

  const run = async () => {
    setState("busy");
    try {
      const res = await fetch("/api/public/unsubscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ t }),
      });
      setState(res.ok ? "done" : "error");
    } catch {
      setState("error");
    }
  };

  return (
    <div className="mx-auto max-w-md py-12">
      <Card>
        <CardHeader>
          <CardTitle as="h2" className="text-xl">
            {state === "done" ? uiText("You're unsubscribed") : uiText("Unsubscribe from Statlign emails")}
          </CardTitle>
          <CardDescription>
            {state === "done"
              ? uiText("We won't send you recap or alert emails any more. Payment receipts still arrive when you buy.")
              : uiText("Stop daily recap and alert emails. You can turn them back on any time in Settings.")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {!t ? (
            <p className="text-sm text-muted-foreground">
              {uiText("This link is incomplete. Sign in and switch off email updates in Settings.")}
            </p>
          ) : state !== "done" ? (
            <Button className="w-full" onClick={run} disabled={state === "busy"}>
              {state === "busy" ? uiText("Unsubscribing…") : uiText("Unsubscribe")}
            </Button>
          ) : null}
          {state === "error" && (
            <p className="text-sm text-destructive">
              {uiText("We couldn't confirm this link. Sign in and switch off email updates in Settings.")}
            </p>
          )}
          <Button asChild variant="outline" className="w-full">
            <Link to="/settings" hash="notifications">{uiText("Open notification settings")}</Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
